import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Book as BookIcon, 
  Style as StyleIcon, 
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  PlayArrow as PlayArrowIcon,
  UploadFile as UploadFileIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import apiService from '../services/api';
import FlashcardsModal from '../components/FlashcardsModal';
import RelearnContentCard from '../components/RelearnContentCard';
import { FlashcardsSkeleton } from '../components/SkeletonLoader';

const Flashcards = () => {
  const { subject } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [showFlashcards, setShowFlashcards] = useState(false);
  const [existingContent, setExistingContent] = useState({});
  const [contentLoading, setContentLoading] = useState({});
  const [generatingContent, setGeneratingContent] = useState({});
  const [currentFlashcardData, setCurrentFlashcardData] = useState(null);
  
  // Relearn card state
  const [showRelearnCard, setShowRelearnCard] = useState(false);
  const [relearnCardData, setRelearnCardData] = useState({
    contentType: null,
    subject: null,
    chapter: null
  });

  useEffect(() => {
    getFlashcardMaterials();
  }, [subject]);

  const getFlashcardMaterials = async () => {
    try {
      // Use OpenAI API to generate chapters
      const response = await apiService.generateChapters(subject);
      const chapters = response.chapters || [];
      setChapters(chapters.map((chapter, index) => ({
        id: index + 1,
        title: chapter,
        type: 'chapter'
      })));
    } catch (error) {
      console.error('Error loading flashcard materials:', error);
      setChapters([]);
    } finally {
      setLoading(false);
    }
  };

  const getExistingContent = async (chapter) => {
    setContentLoading(prev => ({ ...prev, [chapter.title]: true }));
    
    try {
      const response = await apiService.getExistingContent('flashcard', subject, chapter.title, 1, 10);
      setExistingContent(prev => ({
        ...prev,
        [chapter.title]: {
          items: response.items || [],
          total: response.total || 0,
          currentPage: 1
        }
      }));
      
    } catch (error) {
      console.error('Error fetching existing flashcard content:', error);
      setExistingContent(prev => ({
        ...prev,
        [chapter.title]: {
          items: [],
          total: 0,
          currentPage: 1
        }
      }));
    } finally {
      setContentLoading(prev => ({ ...prev, [chapter.title]: false }));
    }
  };

  // Function to relearn existing content
  const handleRelearn = async (contentId, contentData) => {
    try {
      const response = await apiService.relearnContent('flashcard', contentId);
      setCurrentFlashcardData({
        flashcards: response.flashcards || [],
        isExisting: true,
        contentId: contentId,
        contentData: contentData
      });
      setSelectedChapter(selectedChapter);
      setShowFlashcards(true);
    } catch (error) {
      console.error('Error relearning content:', error);
    }
  };

  // Function to generate new content
  const handleGenerateNew = async (chapter) => {
    setGeneratingContent(prev => ({ ...prev, [chapter.title]: true }));
    
    try {
      const response = await apiService.generateFlashcards(subject, chapter.title);
      setCurrentFlashcardData({
        flashcards: response.flashcards || [],
        isExisting: false,
        existingContent: response.existing_content || [],
        totalExisting: response.total_existing || 0
      });
      setSelectedChapter(chapter);
      setShowFlashcards(true);
      
      // Refresh existing content list
      await getExistingContent(chapter);
      
    } catch (error) {
      console.error('Error generating new flashcards:', error);
    } finally {
      setGeneratingContent(prev => ({ ...prev, [chapter.title]: false }));
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleChapterFlashcards = async (chapter) => {
    console.log('🔍 Chapter flashcards clicked:', chapter);
    
    // Show relearn card for flashcard content
    setRelearnCardData({
      contentType: 'flashcard',
      subject: subject,
      chapter: chapter.title
    });
    setShowRelearnCard(true);
  };

  const handleCreateCustomFlashcards = () => {
    // Navigate to upload page for creating custom flashcards
    navigate(`/upload/${encodeURIComponent(subject)}`);
  };

  // Relearn card handlers
  const handleRelearnContent = (contentType, response, originalContent) => {
    if (contentType === 'flashcard') {
      setCurrentFlashcardData({
        flashcards: response.flashcards || [],
        isExisting: true,
        contentId: originalContent.id,
        contentData: originalContent
      });
      setSelectedChapter({ title: relearnCardData.chapter });
      setShowFlashcards(true);
    }
    setShowRelearnCard(false);
  };

  const handleRegenerateContent = (contentType, response) => {
    if (contentType === 'flashcard') {
      setCurrentFlashcardData({
        flashcards: response.flashcards || [],
        isNew: true
      });
      setSelectedChapter({ title: relearnCardData.chapter });
      setShowFlashcards(true);
    }
    setShowRelearnCard(false);
  };

  const handleCloseRelearnCard = () => {
    setShowRelearnCard(false);
    setRelearnCardData({
      contentType: null,
      subject: null,
      chapter: null
    });
  };

  if (loading) {
    return <FlashcardsSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Go back"
              >
                <ArrowBackIcon className="text-gray-600 dark:text-gray-300" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {subject} - Flashcard Center
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Practice with flashcards to reinforce your learning
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-sm font-medium">
                {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Learner'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Quick Actions */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Quick Actions
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleCreateCustomFlashcards}
              className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-200 text-left group"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg group-hover:bg-purple-200 dark:group-hover:bg-purple-800 transition-colors">
                  <UploadFileIcon className="text-purple-600 dark:text-purple-400 text-2xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    Create Custom Flashcards
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Upload your own content and create personalized flashcards
                  </p>
                </div>
              </div>
            </motion.button>
          </div>
        </div>

        {/* Chapter Flashcards */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            Chapter Flashcards
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {chapters.map((chapter, index) => (
              <motion.div
                key={chapter.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-200"
              >
                {/* Chapter Header */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                        <StyleIcon className="text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {chapter.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {chapter.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-4 space-y-2">
                    <button
                      onClick={() => handleChapterFlashcards(chapter)}
                      disabled={contentLoading[chapter.title] || generatingContent[chapter.title]}
                      className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      {contentLoading[chapter.title] ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Loading...
                        </>
                      ) : generatingContent[chapter.title] ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <PlayArrowIcon className="text-sm" />
                          Start Flashcards
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Relearn Content Card */}
      {showRelearnCard && relearnCardData.contentType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="max-w-2xl w-full">
            <RelearnContentCard
              subject={relearnCardData.subject}
              chapter={relearnCardData.chapter}
              contentType={relearnCardData.contentType}
              onRelearn={handleRelearnContent}
              onRegenerate={handleRegenerateContent}
              onClose={handleCloseRelearnCard}
            />
          </div>
        </div>
      )}

      {/* Flashcards Modal */}
      {showFlashcards && selectedChapter && currentFlashcardData && (
        <FlashcardsModal
          isOpen={showFlashcards}
          onClose={() => {
            setShowFlashcards(false);
            setSelectedChapter(null);
            setCurrentFlashcardData(null);
          }}
          subject={subject}
          chapter={selectedChapter.title}
          flashcards={currentFlashcardData.flashcards}
          isExisting={currentFlashcardData.isExisting}
          contentId={currentFlashcardData.contentId}
          existingContent={currentFlashcardData.existingContent}
          totalExisting={currentFlashcardData.totalExisting}
          onRegenerate={() => handleGenerateNew(selectedChapter)}
        />
      )}
    </div>
  );
};

export default Flashcards; 